import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Save, ShieldAlert, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { invalidateFeeConfig } from "@/lib/fees";

export const Route = createFileRoute("/dashboard/admin")({
  head: () => ({
    meta: [{ title: "Admin · Configuración financiera — ZYNTRA" }],
  }),
  component: AdminPage,
});

interface FormState {
  service_fee_percent: string;
  service_fee_fixed: string;
  resale_platform_fee_percent: string;
  resale_payment_fee_percent: string;
  resale_payment_fee_fixed: string;
  max_resale_markup_percent: string;
  currency: string;
}

const DEFAULTS: FormState = {
  service_fee_percent: "10",
  service_fee_fixed: "500",
  resale_platform_fee_percent: "7",
  resale_payment_fee_percent: "3",
  resale_payment_fee_fixed: "0",
  max_resale_markup_percent: "110",
  currency: "CRC",
};

function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data: roleRow } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!roleRow);
      const { data } = await supabase
        .from("platform_settings")
        .select(
          "service_fee_percent, service_fee_fixed, resale_platform_fee_percent, resale_payment_fee_percent, resale_payment_fee_fixed, max_resale_markup_percent, currency"
        )
        .limit(1)
        .single();
      if (data) {
        setForm({
          service_fee_percent: String(data.service_fee_percent),
          service_fee_fixed: String(data.service_fee_fixed),
          resale_platform_fee_percent: String(data.resale_platform_fee_percent),
          resale_payment_fee_percent: String(data.resale_payment_fee_percent),
          resale_payment_fee_fixed: String(data.resale_payment_fee_fixed ?? 0),
          max_resale_markup_percent: String(data.max_resale_markup_percent),
          currency: data.currency || "CRC",
        });
      }
      setLoading(false);
    })();
  }, [user, authLoading]);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function validatePercent(value: number, label: string, max = 100): string | null {
    if (!Number.isFinite(value) || value < 0 || value > max) {
      return `${label} debe estar entre 0 y ${max}.`;
    }
    return null;
  }

  function validateNonNegative(value: number, label: string): string | null {
    if (!Number.isFinite(value) || value < 0) return `${label} no puede ser negativo.`;
    return null;
  }

  async function save() {
    setSaving(true);
    setMsg(null);

    const sp = Number(form.service_fee_percent);
    const sf = Number(form.service_fee_fixed);
    const rpp = Number(form.resale_platform_fee_percent);
    const rpay = Number(form.resale_payment_fee_percent);
    const rpayFix = Number(form.resale_payment_fee_fixed);
    const markup = Number(form.max_resale_markup_percent);

    const errors = [
      validatePercent(sp, "Cargo de servicio (%)"),
      validateNonNegative(sf, "Cargo de servicio fijo"),
      validatePercent(rpp, "Comisión reventa plataforma (%)"),
      validatePercent(rpay, "Comisión reventa pago (%)"),
      validateNonNegative(rpayFix, "Comisión reventa pago fija"),
      validatePercent(markup, "Markup máximo de reventa (%)", 500),
    ].filter(Boolean) as string[];

    if (markup < 100) errors.push("El markup máximo no puede ser menor a 100% (precio base).");
    if (rpp + rpay >= 100) errors.push("La suma de comisiones de reventa no puede ser ≥ 100%.");

    if (errors.length) {
      setMsg({ type: "err", text: errors.join(" ") });
      setSaving(false);
      return;
    }

    const { data: existing } = await supabase
      .from("platform_settings")
      .select("id")
      .limit(1)
      .single();
    const { error } = await supabase
      .from("platform_settings")
      .update({
        service_fee_percent: sp,
        service_fee_fixed: sf,
        resale_platform_fee_percent: rpp,
        resale_payment_fee_percent: rpay,
        resale_payment_fee_fixed: rpayFix,
        max_resale_markup_percent: markup,
        currency: form.currency,
      })
      .eq("id", existing!.id);
    if (error) {
      setMsg({ type: "err", text: error.message });
    } else {
      invalidateFeeConfig();
      setMsg({ type: "ok", text: "Configuración guardada." });
    }
    setSaving(false);
  }

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || isAdmin === false) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Solo administradores pueden configurar la plataforma.
        </p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  // Vista previa con datos del form
  const basePrice = 10000;
  const sp = Number(form.service_fee_percent) || 0;
  const sf = Number(form.service_fee_fixed) || 0;
  const rpp = Number(form.resale_platform_fee_percent) || 0;
  const rpay = Number(form.resale_payment_fee_percent) || 0;
  const rpayFix = Number(form.resale_payment_fee_fixed) || 0;
  const markup = Number(form.max_resale_markup_percent) || 100;

  const purchaseFee = Math.round((basePrice * sp) / 100) + sf;
  const totalPaid = basePrice + purchaseFee;
  const maxResale = Math.round((basePrice * markup) / 100);
  const resaleFees = Math.round((maxResale * (rpp + rpay)) / 100) + rpayFix;
  const netSeller = Math.max(0, maxResale - resaleFees);
  const profit = netSeller - totalPaid;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-24 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al dashboard
      </Link>
      <Link
        to="/admin/tenants"
        className="mb-4 ml-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
      >
        Gestionar tenants →
      </Link>
      <Link
        to="/admin/health"
        className="mb-4 ml-3 inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
      >
        📊 Salud operacional →
      </Link>

      {/* Compra primaria */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <h1 className="text-xl font-bold text-foreground">Configuración financiera</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Estos valores se aplican globalmente y los lee tanto el frontend como el backend en tiempo real.
        </p>

        <h2 className="mt-6 text-sm font-bold uppercase tracking-wide text-foreground">
          Compra primaria
        </h2>
        <p className="text-xs text-muted-foreground">
          Cargo aplicado a cada entrada vendida: <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">precio × % + fijo</code>.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field
            label="Cargo de servicio (%)"
            value={form.service_fee_percent}
            onChange={(v) => update("service_fee_percent", v)}
            type="number"
            step="0.5"
          />
          <Field
            label={`Cargo fijo (${form.currency})`}
            value={form.service_fee_fixed}
            onChange={(v) => update("service_fee_fixed", v)}
            type="number"
            step="1"
          />
        </div>

        {/* Reventa */}
        <h2 className="mt-8 text-sm font-bold uppercase tracking-wide text-foreground">
          Reventa
        </h2>
        <p className="text-xs text-muted-foreground">
          Comisiones que se descuentan del precio de venta del vendedor.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field
            label="Comisión plataforma (%)"
            value={form.resale_platform_fee_percent}
            onChange={(v) => update("resale_platform_fee_percent", v)}
            type="number"
            step="0.5"
          />
          <Field
            label="Comisión pago (%)"
            value={form.resale_payment_fee_percent}
            onChange={(v) => update("resale_payment_fee_percent", v)}
            type="number"
            step="0.5"
          />
          <Field
            label={`Comisión pago fija (${form.currency})`}
            value={form.resale_payment_fee_fixed}
            onChange={(v) => update("resale_payment_fee_fixed", v)}
            type="number"
            step="1"
          />
          <Field
            label="Markup máximo de reventa (%)"
            value={form.max_resale_markup_percent}
            onChange={(v) => update("max_resale_markup_percent", v)}
            type="number"
            step="1"
            hint="100 = sin markup, 110 = +10% sobre precio base, 120 = +20%."
          />
        </div>

        {/* Moneda */}
        <h2 className="mt-8 text-sm font-bold uppercase tracking-wide text-foreground">
          Moneda
        </h2>
        <div className="mt-4">
          <Field
            label="Código ISO 4217"
            value={form.currency}
            onChange={(v) => update("currency", v.toUpperCase())}
            type="text"
            maxLength={3}
            hint="Default: CRC (Colones de Costa Rica)."
          />
        </div>

        {msg && (
          <p
            className={`mt-4 rounded-lg px-3 py-2 text-xs ${
              msg.type === "ok"
                ? "bg-success/10 text-success"
                : "bg-destructive/10 text-destructive"
            }`}
          >
            {msg.text}
          </p>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar configuración
        </button>

        {/* Vista previa */}
        <div className="mt-8 rounded-xl border border-border bg-surface p-4 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">
            Vista previa con precio base de {basePrice.toLocaleString("es-CR")} {form.currency}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-[11px] font-semibold uppercase text-muted-foreground">Compra</p>
              <Row label="Precio base" value={`${basePrice.toLocaleString("es-CR")} ${form.currency}`} />
              <Row label="Cargo servicio" value={`${purchaseFee.toLocaleString("es-CR")} ${form.currency}`} />
              <Row
                label="Total que paga"
                value={`${totalPaid.toLocaleString("es-CR")} ${form.currency}`}
                bold
              />
            </div>
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-[11px] font-semibold uppercase text-muted-foreground">
                Reventa al máximo permitido
              </p>
              <Row label="Precio máx." value={`${maxResale.toLocaleString("es-CR")} ${form.currency}`} />
              <Row label="− Comisiones" value={`${resaleFees.toLocaleString("es-CR")} ${form.currency}`} />
              <Row
                label="Recibe vendedor"
                value={`${netSeller.toLocaleString("es-CR")} ${form.currency}`}
                bold
              />
              <Row
                label="Diferencia vs total pagado"
                value={`${profit >= 0 ? "+" : "−"}${Math.abs(profit).toLocaleString("es-CR")} ${form.currency}`}
                className={profit >= 0 ? "text-success" : "text-destructive"}
                bold
              />
            </div>
          </div>
          {profit < 0 && (
            <p className="mt-3 text-[11px] text-warning">
              ⚠ Con esta configuración, el vendedor no recupera lo pagado al revender al máximo. Esto es esperable: la UI de reventa se lo informa explícitamente al usuario.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  step?: string;
  maxLength?: number;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-foreground">{props.label}</label>
      <input
        type={props.type}
        step={props.step}
        maxLength={props.maxLength}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
      />
      {props.hint && <p className="mt-1 text-[11px] text-muted-foreground">{props.hint}</p>}
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  className,
}: {
  label: string;
  value: string;
  bold?: boolean;
  className?: string;
}) {
  return (
    <div className="mt-1 flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? "font-bold" : ""} ${className ?? "text-foreground"}`}>{value}</span>
    </div>
  );
}
